/** @file One row of the Tree: indent, expand caret, label or rename input, click, double-click, context menu. */
import type { MouseEvent } from 'react';
import './tokens.css';
import './Tree.css';
import { TextInput } from './TextInput';
import type { DropWhere } from './logic/dropPosition';
import type { TreeRow } from './logic/flattenTree';
import { joinClass } from './logic/joinClass';
import type { Point } from './logic/placeMenu';
import type { RowDragProps } from './useTreeDrag';

/** Callbacks a Tree row can raise; only `onToggle` and `onSelect` are required. */
export interface TreeHandlers {
  onToggle: (id: string, expanded: boolean) => void;
  /** `additive` is true when Ctrl or Cmd was held, `shift` when Shift was (clicks only; the keyboard passes neither). */
  onSelect: (id: string, additive: boolean, shift?: boolean) => void;
  onRenameStart?: ((id: string) => void) | undefined;
  onRenameCommit?: ((id: string, name: string) => void) | undefined;
  onRenameCancel?: (() => void) | undefined;
  /** `at` is the pointer position in viewport pixels, ready for ContextMenu. */
  onContextMenu?: ((id: string, at: Point) => void) | undefined;
}

/** Props of TreeRowItem. */
export interface TreeRowItemProps {
  row: TreeRow;
  domId: string;
  top: number;
  height: number;
  selected: boolean;
  active: boolean;
  renaming: boolean;
  /** Already-translated accessible name of the rename input. */
  renameLabel: string;
  handlers: TreeHandlers;
  /** Drag handlers from useTreeDrag (undefined: the row is not draggable). */
  drag?: RowDragProps | undefined;
  /** Drop indicator drawn on this row while something is dragged over it. */
  dropHint?: DropWhere | null;
}

const INDENT_PX = 14;
const CARET = { open: '▾', closed: '▸' };

/** Absolutely positioned tree row; the Tree decides which rows exist. */
export function TreeRowItem({ row, domId, top, height, selected, active, renaming, renameLabel, handlers, drag, dropHint = null }: TreeRowItemProps) {
  const toggle = (e: MouseEvent): void => {
    e.stopPropagation();
    handlers.onToggle(row.id, !row.expanded);
  };
  return (
    <div
      role="treeitem"
      id={domId}
      aria-level={row.depth + 1}
      aria-selected={selected}
      aria-expanded={row.hasChildren ? row.expanded : undefined}
      className={joinClass('p2-tree-row', selected && 'p2-tree-row-selected', active && 'p2-tree-row-active', dropHint !== null && `p2-tree-row-drop-${dropHint}`)}
      style={{ top, height, paddingLeft: row.depth * INDENT_PX + 4 }}
      onClick={(e) => { handlers.onSelect(row.id, e.ctrlKey || e.metaKey, e.shiftKey); }}
      onDoubleClick={() => { handlers.onRenameStart?.(row.id); }}
      onContextMenu={(e) => { e.preventDefault(); handlers.onContextMenu?.(row.id, { x: e.clientX, y: e.clientY }); }}
      {...drag}
    >
      <span className="p2-tree-caret" aria-hidden="true" onClick={toggle}>{row.hasChildren ? (row.expanded ? CARET.open : CARET.closed) : ''}</span>
      {row.badge !== undefined && <span className="p2-tree-badge" aria-hidden="true">{row.badge}</span>}
      {renaming
        ? <TextInput value={row.label} label={renameLabel} autoFocus className="p2-tree-rename" onCommit={(name) => { handlers.onRenameCommit?.(row.id, name); }} onCancel={handlers.onRenameCancel} />
        : <span className="p2-tree-label">{row.label}</span>}
    </div>
  );
}
